import {
  NATIVE_CONVERSATION_THEMES,
  safeExternalURL,
} from "./domain";

export const MAX_QUICK_CAPTURE_CONTENT_LENGTH = 240;
export const MAX_QUICK_CAPTURE_NOTE_LENGTH = 1_000;

export interface QuickCaptureInput {
  content: string;
  privateNote: string;
  theme: string;
}

export interface QuickCaptureDraft {
  kind: "link" | "text";
  title: string;
  sourceLabel: string;
  sourceURL: string;
  privateNotes: string;
  theme: string;
}

export type QuickCaptureResult =
  | { ok: true; draft: QuickCaptureDraft }
  | {
      ok: false;
      field: "content" | "privateNote" | "theme";
      message: string;
    };

export function parseQuickCapture(
  input: QuickCaptureInput,
): QuickCaptureResult {
  const content = normalizeInlineText(input.content);
  const privateNote = input.privateNote.trim();
  if (!content) {
    return {
      ok: false,
      field: "content",
      message: "Paste a full http:// or https:// link, or type a short thought.",
    };
  }
  if (content.length > MAX_QUICK_CAPTURE_CONTENT_LENGTH) {
    return {
      ok: false,
      field: "content",
      message: `Keep the link or thought to ${
        MAX_QUICK_CAPTURE_CONTENT_LENGTH
      } characters or fewer.`,
    };
  }
  if (privateNote.length > MAX_QUICK_CAPTURE_NOTE_LENGTH) {
    return {
      ok: false,
      field: "privateNote",
      message: `Keep the private note to ${
        MAX_QUICK_CAPTURE_NOTE_LENGTH
      } characters or fewer.`,
    };
  }
  const theme = input.theme.trim();
  if (!NATIVE_CONVERSATION_THEMES.includes(
    theme as (typeof NATIVE_CONVERSATION_THEMES)[number],
  )) {
    return {
      ok: false,
      field: "theme",
      message: "Choose one of Convoful’s Conversation themes.",
    };
  }

  const sourceURL = safeExternalURL(content);
  if (sourceURL) {
    const host = new URL(sourceURL).hostname.replace(/^www\./i, "");
    return {
      ok: true,
      draft: {
        kind: "link",
        title: host,
        sourceLabel: host,
        sourceURL,
        privateNotes: privateNote,
        theme,
      },
    };
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(content)) {
    return {
      ok: false,
      field: "content",
      message: "Only full http:// or https:// links can be captured as links.",
    };
  }
  return {
    ok: true,
    draft: {
      kind: "text",
      title: content,
      sourceLabel: "Quick capture",
      sourceURL: "",
      privateNotes: privateNote || content,
      theme,
    },
  };
}

function normalizeInlineText(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}
